/**
 * What went wrong in this tab lately — Pippi attaches it to every message so nobody has to describe an error banner:
 *   - failed calls to the BO's own APIs (fetch and XHR/axios): method, path WITHOUT credentials, status, and the
 *     server's error message when the body is JSON. Request and response bodies are never kept;
 *   - console.error and uncaught errors / promise rejections.
 * A small rolling buffer (the last 20) plus live subscribers (the Show me timeline). Installed once while Pippi is
 * available; uninstall() puts every patched function back.
 */
import type { FailedCall, RecentError } from './pippiApi';
import { scrubText, scrubUrl } from './pippiContext';

export type WatchEvent =
  | { kind: 'network'; call: FailedCall }
  | { kind: 'console'; message: string }
  | { kind: 'error'; message: string };

const MAX_RECENT = 20;
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const hhmmss = (d = new Date()) => d.toTimeString().slice(0, 8);

let recent: RecentError[] = [];
const listeners = new Set<(e: WatchEvent) => void>();
let restore: (() => void) | null = null;

export const recentErrors = (): RecentError[] => recent.slice();
export function onWatch(fn: (e: WatchEvent) => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }

function emit(e: WatchEvent) {
  const message = e.kind === 'network' ? `${e.call.method} ${e.call.path} → ${e.call.status || 'failed'}${e.call.message ? ` "${e.call.message}"` : ''}` : e.message;
  recent = [...recent, { at: hhmmss(), kind: e.kind, message: clip(message, 300) }].slice(-MAX_RECENT);
  for (const fn of listeners) { try { fn(e); } catch { /* a listener never breaks the page */ } }
}

/** Our own APIs: any *.beatly.com host, this origin, or a local dev server. Pippi's own calls are left out. */
export function isOwnApi(url: string, origin = typeof location !== 'undefined' ? location.origin : 'http://localhost'): boolean {
  try {
    const u = new URL(url, origin);
    if (/\/v1\/pippi\/|\/v1\/calls\/ears-secret/.test(u.pathname)) return false;
    return u.origin === origin || /(^|\.)beatly\.com$/i.test(u.hostname) || /^(localhost|127\.0\.0\.1)$/.test(u.hostname);
  } catch { return false; }
}

/** The path part of a URL (host dropped for our own APIs), credentials stripped. */
export function callPath(url: string): string {
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.origin : 'http://localhost');
    return scrubUrl(`${u.pathname}${u.search}`);
  } catch { return scrubUrl(url); }
}

/** The server's own words from a JSON error body: error / message / detail (FastAPI) / errors[0]. Never the whole body. */
export function errorMessageOf(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  const pick = (v: unknown): string | undefined => {
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && v.length) return pick((v[0] as Record<string, unknown>)?.msg ?? v[0]);
    if (v && typeof v === 'object') return pick((v as Record<string, unknown>).message ?? (v as Record<string, unknown>).msg);
    return undefined;
  };
  const m = pick(b.error) ?? pick(b.message) ?? pick(b.detail) ?? pick(b.errors) ?? pick(b.code);
  return m ? clip(scrubText(m), 160) : undefined;
}

export function reportFailedCall(method: string, url: string, status: number | undefined, message?: string) {
  if (!isOwnApi(url)) return;
  emit({ kind: 'network', call: { at: hhmmss(), method: (method || 'GET').toUpperCase(), path: callPath(url), status: status || 0, ...(message ? { message } : {}) } });
}

/**
 * Message text only: strings, numbers and Error messages. Objects, arrays and DOM nodes are never serialized (they can
 * hold client data); they show as their kind, e.g. "[object]".
 */
const textOf = (a: unknown): string => {
  if (typeof a === 'string') return a;
  if (typeof a === 'number' || typeof a === 'boolean' || a == null) return String(a);
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  return Array.isArray(a) ? '[array]' : '[object]';
};
const stringify = (args: unknown[]) => clip(scrubText(args.map(textOf).join(' ')), 300);

export function install(win: Window & typeof globalThis = window): () => void {
  if (restore) return restore;
  const origFetch = win.fetch;
  const origError = win.console.error;
  const proto = win.XMLHttpRequest?.prototype;
  const origOpen = proto?.open;
  const origSend = proto?.send;
  const meta = new WeakMap<XMLHttpRequest, { method: string; url: string }>();

  win.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    const method = (init?.method || (typeof input === 'object' && 'method' in input ? (input as Request).method : 'GET') || 'GET').toUpperCase();
    try {
      const res = await origFetch.call(win, input as RequestInfo, init);
      if (!res.ok && isOwnApi(url)) {
        const type = res.headers.get('content-type') || '';
        if (type.includes('json')) res.clone().json().then((j) => reportFailedCall(method, url, res.status, errorMessageOf(j)), () => reportFailedCall(method, url, res.status));
        else reportFailedCall(method, url, res.status);
      }
      return res;
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') reportFailedCall(method, url, 0, 'Network error');
      throw e;
    }
  } as typeof win.fetch;

  if (proto && origOpen && origSend) {
    proto.open = function patchedOpen(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
      meta.set(this, { method: String(method || 'GET'), url: String(url) });
      return (origOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof proto.open;
    proto.send = function patchedSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const m = meta.get(this);
      if (m) {
        this.addEventListener('loadend', () => {
          if (this.status && this.status < 400) return;
          let message: string | undefined;
          try {
            const raw = this.responseType === 'json' ? this.response : (this.responseType === '' || this.responseType === 'text') && this.responseText ? JSON.parse(this.responseText) : null;
            message = errorMessageOf(raw);
          } catch { /* not JSON */ }
          reportFailedCall(m.method, m.url, this.status, message ?? (this.status ? undefined : 'Network error'));
        });
      }
      return origSend.call(this, body);
    };
  }

  win.console.error = function patchedConsoleError(...args: unknown[]) {
    try { emit({ kind: 'console', message: stringify(args) }); } catch { /* never break logging */ }
    return origError.apply(win.console, args);
  };
  const onError = (e: ErrorEvent) => emit({ kind: 'error', message: clip(scrubText(e.message || String(e.error || 'Error')), 300) });
  const onRejection = (e: PromiseRejectionEvent) => emit({ kind: 'error', message: `Unhandled: ${stringify([e.reason])}` });
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);

  restore = () => {
    win.fetch = origFetch;
    win.console.error = origError;
    if (proto && origOpen && origSend) { proto.open = origOpen; proto.send = origSend; }
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
    restore = null;
    recent = [];
  };
  return restore;
}
